<script lang="ts">
import { useGridObserver } from '@/plugins/useGridObserver'
import type { InstantWinDraw } from '@/types'
import type { TIconValue } from '@/utils/helper-objects'
import { type PropType, defineComponent } from 'vue'
import type { RouteLocationRaw } from 'vue-router'

import AppCarousel from './AppCarousel.vue'
import CarouselItem from './CarouselItem.vue'

const TARGET_CARDS_PER_ROW = 4

export default defineComponent({
  components: {
    AppCarousel,
    CarouselItem,
  },
  props: {
    title: {
      type: String,
      required: true,
    },
    framed: {
      type: Boolean,
      required: false,
    },
    prizes: {
      type: Array<InstantWinDraw>,
      required: true,
    },
    icon: {
      type: String as PropType<TIconValue> | null,
    },
    rows: {
      type: Number,
      default: 1,
      validator(value) {
        return value === 1 || value === 2
      },
    },
    viewAllRoute: {
      type: Object as PropType<RouteLocationRaw>,
    },
    remainingColumns: {
      type: Number,
      default: 0,
    },
    columnsPerRow: {
      type: Number,
      default: 4,
    },
  },
  data() {
    return {
      emptySlots: 1,
      emptySlotWidth: 0,
    }
  },

  computed: {
    computedRows() {
      if (this.prizes.length < TARGET_CARDS_PER_ROW * 2) {
        return 1
      }

      return Math.min(this.rows, 2)
    },
    columns() {
      return this.prizeListToColumns(this.prizes, this.computedRows)
    },
  },
  watch: {
    columnsPerRow: {
      handler(newVal) {
        this.emptySlots = this.remainingColumns
        this.emptySlotWidth = (this.remainingColumns / newVal) * 100
      },
      immediate: true,
    },
  },
  mounted() {
    useGridObserver(
      this.$el.querySelector('.scrollbox ul') as HTMLElement,
      ({ computedGridColumns, computedRemainingColumns }) => {
        if (this.remainingColumns) {
          this.emptySlots = this.remainingColumns
          this.emptySlotWidth =
            (this.remainingColumns / this.columnsPerRow) * 100
          return
        }
        this.emptySlots = computedRemainingColumns.value
        this.emptySlotWidth =
          (computedRemainingColumns.value / computedGridColumns.value) * 100
      },
      this.$el.querySelectorAll(
        '.scrollbox li.carousel-item .coming-soon-card'
      ) as HTMLElement[]
    )
  },
  methods: {
    prizeListToColumns<T>(list: T[], perColumn: number): T[][] {
      const columns = []
      for (let c = 0; c < list.length; c += perColumn) {
        columns.push(list.slice(c, c + perColumn))
      }
      return columns
    },
  },
})
</script>

<template>
  <AppCarousel
    :icon="icon"
    :title="title"
    :framed="framed"
    :view-all-route="viewAllRoute"
    class="game-carousel"
    frame-class="-mx-3 @md/main:mx-0 @md/main:rounded-xl rounded-none"
  >
    <template #preamble>
      <slot name="preamble"></slot>
    </template>

    <!-- Single row -->
    <template v-if="computedRows === 1">
      <CarouselItem
        v-for="column in columns"
        :key="column[0].code"
        class="w-full @xs/main:w-1/2 @md/main:w-1/3 @lg/main:w-1/4 display-initial"
      >
        <slot v-for="prize in column" :prize></slot>
      </CarouselItem>

      <!-- Add an optional fallback extra item if there aren't enough elements
      to fill the carousel -->
      <CarouselItem
        v-if="emptySlots"
        class="hidden @xs/main:inline"
        :style="{ width: `${emptySlotWidth}%` }"
      >
        <slot name="empty" :empty-slots></slot>
      </CarouselItem>
    </template>

    <!-- Double row -->
    <template v-if="computedRows === 2">
      <CarouselItem
        v-for="(column, index) in columns"
        :key="column[0].code"
        class="flex w-1/2 flex-col gap-3 @md/main:w-1/3 @lg/main:w-1/4"
      >
        <slot v-for="prize in column" :prize></slot>
        <slot
          v-if="emptySlots && index === columns.length - 1"
          name="empty"
          :empty-slots
        ></slot>
      </CarouselItem>
    </template>
  </AppCarousel>
</template>
